import { Button, Grid2, IconButton, Stack, Typography } from "@mui/material";
import ContainerLoading from "../components/ContainerLoading";
import { useLocation, useNavigate, useParams } from "react-router";
import { IconCheck, IconCircleX, IconPlus } from "@tabler/icons-react";
import {
  classesConfirm,
  getClassesById,
  removeCheckIn,
} from "../services/classes";
import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment";
import { toast } from "react-toastify";
import { useState } from "react";
import StudentsList from "../components/StudentsList";
import PageHeader from "../components/PageHeader";
import { Empty } from "../components/Empty";

const AttendanceList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [open, setOpen] = useState(false);
  const [studentId, setStudentId] = useState<null | number>(null);

  const {
    data,
    isLoading,
    isFetching,
    refetch,
  }: {
    data: any;
    isLoading: boolean;
    isFetching: boolean;
    refetch: () => void;
  } = useQuery({
    queryKey: ["fightById"],
    queryFn: async () => await getClassesById(Number(id)),
    initialData: {},
  });

  const { mutate, isPending } = useMutation({
    mutationFn: classesConfirm,
    onSuccess: () => {
      handleBack();
      toast.success("Aula atualizada com sucesso");
    },

    onError: (e: { response: { data: { message: string } } }) => {
      toast.error(String(e?.response?.data?.message) || "Erro!");
    },
  });

  const { mutate: mutateRemoveCheckin, isPending: isPendingRemoveCheckin } =
    useMutation({
      mutationFn: async () =>
        await removeCheckIn(Number(id), { studentId: studentId }),
      onSuccess: () => {
        refetch();
        toast.success("Check-in removido");
      },

      onError: (e: { response: { data: { message: string } } }) => {
        toast.error(String(e?.response?.data?.message) || "Erro!");
      },
    });

  const handleBack = () => {
    const prevPath = location.state?.from;

    if (prevPath) {
      navigate(prevPath, {
        state: location.state,
      });
      return;
    }

    navigate(-1);
  };

  const loading =
    isLoading || isFetching || isPending || isPendingRemoveCheckin;

  return (
    <ContainerLoading loading={loading}>
      <Grid2 container spacing={2} padding={2}>
        <PageHeader
          title="Detalhes da aula"
          subtitle="Informações gerais da aula."
          back
          options={
            <Stack>
              {data?.status === "PENDING" &&
                moment().isSameOrAfter(data.date, "date") && (
                  <Button
                    variant="contained"
                    sx={{
                      gap: 0.5,
                    }}
                    onClick={() => mutate({ id: data.id })}
                    endIcon={<IconCheck size={18} />}
                  >
                    <Typography variant="body2">Finalizar aula</Typography>
                  </Button>
                )}
            </Stack>
          }
        />

        <Grid2 size={12} mb={2}>
          <Stack
            spacing={2}
            sx={{
              backgroundColor: "secondary.dark",
              padding: 2,
              borderRadius: 2,
            }}
          >
            <Stack sx={{ flexDirection: "row", alignItems: "center", gap: 1 }}>
              <Typography
                variant="body1"
                fontWeight={600}
                fontSize={14}
                sx={{ color: "secondary.contrastText" }}
              >
                Data
              </Typography>
              <Typography variant="body1" fontSize={14}>
                {moment(data?.date).format("DD/MM/YYYY")}
              </Typography>
            </Stack>
            {data?.fightsId && (
              <Stack
                sx={{ flexDirection: "row", alignItems: "center", gap: 1 }}
              >
                <Typography
                  variant="body1"
                  fontWeight={600}
                  fontSize={14}
                  sx={{ color: "secondary.contrastText" }}
                >
                  Modalidade
                </Typography>
                <Typography variant="body1" fontSize={14}>
                  {data?.fights?.name}
                </Typography>
              </Stack>
            )}
            {data?.professors?.map((i: any) => (
              <Stack
                key={i?.professor?.id}
                sx={{ flexDirection: "row", alignItems: "center", gap: 1 }}
              >
                <Typography
                  variant="body1"
                  fontWeight={600}
                  fontSize={14}
                  sx={{ color: "secondary.contrastText" }}
                >
                  Professor
                </Typography>
                <Typography variant="body1" fontSize={14}>
                  {i?.professor?.fullName}
                </Typography>
              </Stack>
            ))}
            {data?.spacesId && (
              <Stack
                sx={{ flexDirection: "row", alignItems: "center", gap: 1 }}
              >
                <Typography
                  variant="body1"
                  fontWeight={600}
                  fontSize={14}
                  sx={{ color: "secondary.contrastText" }}
                >
                  Espaço
                </Typography>
                <Typography variant="body1" fontSize={14}>
                  {data?.spaces?.name}
                </Typography>
              </Stack>
            )}
            {Boolean(data?.class?.limit) && (
              <Stack
                sx={{ flexDirection: "row", alignItems: "center", gap: 1 }}
              >
                <Typography
                  variant="body1"
                  fontWeight={600}
                  fontSize={14}
                  sx={{ color: "secondary.contrastText" }}
                >
                  Limite
                </Typography>
                <Typography variant="body1" fontSize={14}>
                  {data?.frequency?.length} de {data?.class?.limit}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Grid2>
        <Grid2 size={12}>
          <Stack
            sx={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="body1" fontWeight={600} fontSize={18}>
              Alunos
            </Typography>
            <Button
              variant="contained"
              sx={{
                gap: 0.5,
                backgroundColor: "secondary.main",
              }}
              onClick={() => setOpen(true)}
              endIcon={<IconPlus size={18} />}
            >
              <Typography variant="body2">Adicionar aluno</Typography>
            </Button>
          </Stack>
        </Grid2>
        <Grid2 size={12} mb={2}>
          <Stack spacing={2}>
            {data?.frequency?.map((i: any) => (
              <Stack
                key={i?.id}
                sx={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: "secondary.dark",
                  padding: 2,
                  borderRadius: 2,
                }}
              >
                <Stack spacing={1}>
                  <Typography variant="body1" fontWeight={600} fontSize={14}>
                    {i?.student?.fullName}
                  </Typography>
                  {i?.student?.nickName && (
                    <Typography
                      variant="body1"
                      fontSize={12}
                      sx={{ color: "secondary.contrastText" }}
                    >
                      {i?.student?.nickName}
                    </Typography>
                  )}
                </Stack>
                <Stack
                  sx={{ flexDirection: "row", alignItems: "center", gap: 1 }}
                >
                  <Stack
                    sx={{ flexDirection: "row", alignItems: "center", gap: 1 }}
                  >
                    {i.status === "CHECK_IN" && (
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        fontSize={14}
                      >
                        Check-in
                      </Typography>
                    )}
                    {i.status === "APPROVED" && (
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        fontSize={14}
                      >
                        Presente
                      </Typography>
                    )}
                  </Stack>
                  <Stack>
                    <IconButton
                      onClick={() => {
                        setStudentId(i?.student?.id);
                        mutateRemoveCheckin();
                      }}
                    >
                      <IconCircleX size={18} color="red" />
                    </IconButton>
                  </Stack>
                </Stack>
              </Stack>
            ))}

            <Empty data={data?.frequency} />
          </Stack>
        </Grid2>
      </Grid2>
      <StudentsList
        id={Number(id)}
        frequency={data?.frequency}
        open={open}
        onClose={() => setOpen(false)}
        refetch={() => refetch()}
      />
    </ContainerLoading>
  );
};

export default AttendanceList;
